import React, { ErrorInfo, ReactNode } from 'react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="max-w-xl w-full bg-white rounded-2xl shadow-xl p-8 border border-red-100">
            <div className="text-center mb-6">
               <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
               </div>
               <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
               <p className="text-gray-500 mt-2">The application encountered an unexpected error.</p>
            </div>
            
            <div className="bg-gray-900 rounded-xl p-4 overflow-auto max-h-60 mb-6">
                <p className="text-red-400 font-mono text-xs break-all">
                    {this.state.error && this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                    <pre className="text-gray-500 font-mono text-[10px] mt-2 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                    </pre>
                )}
            </div>

            <div className="flex gap-4">
                <button 
                    onClick={() => window.location.reload()}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-4 rounded-xl transition-colors"
                >
                    Reload Page
                </button>
                <button 
                    onClick={() => window.location.href = '/'}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold py-3 px-4 rounded-xl transition-colors"
                >
                    Go Home
                </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}